import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import CollapsibleSection from "../../pages/common-features/CollapsibleSection";
import DetailRow from "../../pages/common-features/DetailRow";
import SummarySectionHeader from "../../components/applicantDetailsTabs/SummarySectionHeader";

interface CollateralDetailsTabProps {
  collateralDetails: any[];
  isMobile: boolean;
}

const CollateralDetailsTab: React.FC<CollateralDetailsTabProps> = ({
  collateralDetails,
  isMobile,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!collateralDetails || collateralDetails.length === 0)
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );

  const sortFields = (fields: any[], isProperty: boolean) => {
    if (!fields) return [];
    const orderPriority: { [key: string]: number } = {
      "collateral type": 0,
      "per price unit": 1,
      "price per unit": 1,
      "number of units": 2,
      "no of units": 2,
      "collateral value": 3,
      totalValue: 3,
      "property type": 4,
      "property sub type": 5,
      address: 6,
      "address line 1": 6,
      "address line 2": 7,
      city: 8,
      "city name": 8,
      state: 9,
      "state name": 9,
      country: 10,
      "country name": 10,
      pincode: 11,
      "co owner name": 20,
      "owner ship percentage": 21,
      "ownership percentage": 21,
      "phone number": 22,
    };

    const filteredFields = fields.filter((field: any) => {
      const name = field.name || "";
      if (name === "totalValue") {
        return isProperty;
      }

      return (
        name !== "addressType" &&
        name !== "landmark" &&
        name !== "area"
      );
    });

    return filteredFields
  };

  const getOwnershipFieldOrder = (field: any) => {
    const label = String(field?.label || "").toLowerCase().replace(/\s+/g, "");
    const name = String(field?.name || "").toLowerCase();

    if (name.includes("coownername") || name.endsWith("name") || label === "coownername" || label === "name") return 1;
    if (name.includes("percentage") || label.includes("percentage")) return 2;
    if (name.includes("phone") || label.includes("phone")) return 3;
    return 99;
  };

  const normalizeOwnershipField = (field: any) => {
    const label = String(field?.label || "").toLowerCase().replace(/\s+/g, "");
    const name = String(field?.name || "").toLowerCase();

    if (name.includes("coownername") || name.endsWith("name") || label === "coownername" || label === "name") {
      return { ...field, label: "Name" };
    }
    if (name.includes("percentage") || label.includes("percentage")) {
      return { ...field, label: "Ownership Percentage" };
    }
    if (name.includes("phone") || label.includes("phone")) {
      return { ...field, label: "Phone Number" };
    }
    return field;
  };

  const subHeaderStyle = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#6f6b7d",
    mb: 3,
  };

  const isHasCollateralField = (field: any) =>
    String(field?.name || field?.fieldKey || "")
      .trim()
      .toLowerCase() === "hascollateral";

  const getHasCollateralField = (items: any[]) =>
    (items || []).find(isHasCollateralField);

  const hasCollateralFieldFromPayload = collateralDetails
    .map((collateralItem: any) => {
      const collateralKey = Object.keys(collateralItem || {})[0];
      const block = collateralItem?.[collateralKey] || {};
      return getHasCollateralField(block.collateral || []);
    })
    .find(Boolean);
  const isHasCollateralNo =
    String(hasCollateralFieldFromPayload?.value ?? "")
      .trim()
      .toLowerCase() === "no";
  const collateralDetailsToRender = isHasCollateralNo ? [] : collateralDetails;

  return (
    <Grid item xs={12}>
      {hasCollateralFieldFromPayload && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <DetailRow
              item={{
                label: "Has Collateral",
                value: hasCollateralFieldFromPayload?.value,
                isVerified: false,
              }}
            />
          </Grid>
        </Grid>
      )}
      {collateralDetailsToRender.map((collateralItem: any, index: number) => {
        const collateralKey = Object.keys(collateralItem)[0];
        const collateralNum = collateralKey.replace(/collateral/i, "");
        const block = collateralItem[collateralKey] || {};
        const collateral = (block.collateral || []).filter(
          (field: any) => !isHasCollateralField(field)
        );
        const ownership = block.ownership || [];

        const isProperty =
          Array.isArray(collateral) &&
          collateral.some((field: any) => {
            const label = (field.label || "").toLowerCase();
            const name = (field.name || "").toLowerCase();
            const value = (field.value || "").toString().toLowerCase();

            return (
              (name === "collateraltype" || label === "collateral type") &&
              value.includes("property")
            );
          });
        const sortedCollateralFields = sortFields(collateral, isProperty);
        const isExpanded = expandedIndex === index;

        return (
          <Box
            key={collateralKey}
	            sx={{ mb: index < collateralDetailsToRender.length - 1 ? 1 : 0 }}
          >
            <CollapsibleSection
              title={`Collateral ${index + 1}`}
              isExpanded={isExpanded}
              onToggle={() => setExpandedIndex(isExpanded ? null : index)}
            >
              {sortedCollateralFields.length > 0 && (
                <Box sx={{ mb: ownership?.length ? 4 : 0 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{ mt: 2, display: 'flex', alignItems: 'center', marginBottom: "16px" }}
                  >
                    <SummarySectionHeader title="Collateral Information" />
                  </Grid>

                  {/* <Typography sx={{ ...subHeaderStyle }}>
                    Collateral Information
                  </Typography> */}
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      ml: isMobile ? 0.2 : -2,
                      width: isMobile ? 'auto' : 'calc(100% + 16px)',
                    }}
                  >
                    {sortedCollateralFields.map(
                      (field: any, fieldIndex: number) => {
                            const isTotalValueField = /^(collateral\d+)?totalValue$/i.test(
                          String(field.name || "")
                        );
                        const isCollateralValueField = /^collateral\d+Value$/i.test(
                          String(field.name || "")
                        );
                        const displayLabel =
                           isTotalValueField
                            ? 'Collateral Value'
                            : field.label;
                        const totalValueNumber = Number(
                          String(field.value ?? "").replace(/,/g, "")
                        );
                        const displayValue =
                           (isTotalValueField || isCollateralValueField) &&
                          field.value !== "" &&
                          field.value !== null &&
                          field.value !== undefined &&
                          Number.isFinite(totalValueNumber)
                            ? new Intl.NumberFormat("en-IN", {
                                style: "currency",
                                currency: "INR",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2,
                              })
                                .format(totalValueNumber)
                                .replace("₹", "₹\u00A0")
                            : field.value;

                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            key={`${collateralKey}-field-${fieldIndex}`}
                          >
                            <DetailRow
                              item={{
                                label: displayLabel === 'Fd Holder Name' ? 'FD Holder Name' : displayLabel === 'Fd Number'? 'FD Number': displayLabel,
                                value: displayValue,
                                isVerified: false,
                              }}
                            />
                          </Grid>
                        );
                      }
                    )}
                  </Grid>
                </Box>
              )}

              {ownership && ownership.length > 0 && (
                <Box>
                  <Grid
                    item
                    xs={12}
                    sx={{ mt: 2, display: 'flex', alignItems: 'center', marginBottom: "16px" }}
                  >
                    <SummarySectionHeader title="Co-Owner Information" />
                  </Grid>
                  {/* <Typography sx={{ ...subHeaderStyle }}>
                    Co-Owner Information
                  </Typography> */}
                  {ownership.map((ownershipItem: any) => {
                    const ownershipKey = Object.keys(ownershipItem)[0];
                    const ownershipFields = ownershipItem[ownershipKey];
                    const sortedOwnershipFields = sortFields(
                      ownershipFields,
                      false
                    )
                      .sort(
                        (a: any, b: any) =>
                          getOwnershipFieldOrder(a) - getOwnershipFieldOrder(b)
                      )
                      .map(normalizeOwnershipField);
                    return (
                      <Grid
                        container
                        spacing={2}
                        key={`${collateralKey}-${ownershipKey}`}
                        sx={{
                          ml: isMobile ? 0.2 : -2,
                          width: isMobile ? 'auto' : 'calc(100% + 16px)',
                          mb: 2,
                        }}
                      >
                        {sortedOwnershipFields.map(
                          (field: any, fieldIndex: number) => (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              md={3}
                              key={`${ownershipKey}-field-${fieldIndex}`}
                            >
                              <DetailRow
                                item={{
                                  label: field.label,
                                  value: field.value,
                                  isVerified: false,
                                }}
                              />
                            </Grid>
                          )
                        )}
                        <Grid
                          item
                          md={3}
                          sx={{ display: { xs: 'none', md: 'block' } }}
                          aria-hidden="true"
                        />
                      </Grid>
                    );
                  })}
                </Box>
              )}
            </CollapsibleSection>
          </Box>
        );
      })}
    </Grid>
  );
};

export default CollateralDetailsTab;
